import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Analytics } from 'lib/AnalyticsLoader/analytics';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';
import { useMediaQuery } from 'usehooks-ts';

import { SignModal } from 'modules/ConnectPage/components/SignModal/SignModal';

import { fetchGamesApi } from 'shared/api/gamesApi';
import { ConnectWallet } from 'shared/components/sections/connect-wallet/connect-wallet';
import { RpcErrorModal } from 'shared/components/sections/rpc-error-modal/rpc-error-modal';
import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { ROUTES } from 'shared/constants';
import { useGamesInfo } from 'shared/hooks/use-games-info-from-api';
import { useStartJoinAllowance } from 'shared/hooks/use-start-join-allowance';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useShowSignModal } from 'shared/hooks/useShowSignModal';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGlobalStore } from 'shared/store/globalStore';
import { useQuestsStore } from 'shared/store/questsStore';
import { useStartGameStore } from 'shared/store/start-game-store';
import { useUIStore } from 'shared/store/uiStore';
import { CurrencyType } from 'shared/web3/utils/getCurrencyInfo';

import { DesktopView, MobileView } from './views';

export type Game = [
  id: number,
  playerA: `0x${string}`,
  playerB: `0x${string}`,
  token: `0x${string}`,
  bet: `0x${string}`,
  wins: string,
];

//todo: use fetch games from api for render all games
export const LobbyPage = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const invitedBy = useGlobalStore((state) => state.invitedBy);

  const { address, isConnected, chainId } = useAccount();
  const publicClient = usePublicClient();
  const walletClient = useWalletClient();

  const { showSignModal } = useShowSignModal();
  const { rpcErrorModal } = useUIStore();
  const { setSnapshotPoints } = useQuestsStore();
  const { approveAllowanceHandler, joinRoomHandler } = useStartJoinAllowance();
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [gamesActive, setGamesActive] = useState<Game[]>([]);
  const { gamesInfo, loading, error } = useGamesInfo(gamesActive, publicClient);

  const { startGame, fetchCurrentAllowance } = useStartGameStore();
  const { selectedCurrency } = useCurrenciesStore();

  const lastAllowanceKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!publicClient || !selectedCurrency || !address) return;
    if (selectedCurrency.chainId !== chainId) return;

    const key = `${address}:${selectedCurrency.address}:${chainId}`;
    if (lastAllowanceKeyRef.current === key) return; // already fetched for this combo

    lastAllowanceKeyRef.current = key;
    fetchCurrentAllowance(address, selectedCurrency, publicClient);
  }, [publicClient, selectedCurrency, address, chainId]);

  const startGameHandler = useCallback(
    async (bet: number, wins: number, playerB: string, currency: CurrencyType) => {
      if (!walletClient || !publicClient || !selectedCurrency || !bet) return;
      audioManager.playSound(AUDIO_TYPES.CONFIRM_WALLET);
      let usedCurrency = currency || selectedCurrency;
      let output = await startGame({
        wins,
        currency: usedCurrency.address,
        betAmount: BigInt(Math.floor(+bet * 10 ** usedCurrency!.decimals)),
        playerB: playerB as `0x${string}`,
        referrer: invitedBy,
        walletClient,
        publicClient,
        userAddress: address as `0x${string}`,
      });
      if (output) {
        setSnapshotPoints();
        Analytics.createRoomEvent(address, invitedBy, bet, usedCurrency.symbol);
        setTimeout(() => {
          navigate(`${ROUTES.ROOM}/${chainId}/${output}`);
        }, 1000);
      }
    },
    [
      walletClient,
      publicClient,
      selectedCurrency,
      address,
      invitedBy,
      navigate,
      chainId,
      startGame,
      Analytics,
    ],
  );

  useEffect(() => {
    const fetchGames = async () => {
      if (!chainId) return;
      let games = await fetchGamesApi(chainId);
      const activeGames =
        games.filter(
          (game) =>
            (game[1] === address || game[2] === address) &&
            (game[5] === 'open' || game[5] === 'playing'),
        ) || [];
      games = games.filter((game) => game[5] === 'playing' || game[5] === 'completed').slice(0, 20);
      setGames(games);
      setGamesActive(activeGames);
    };
    fetchGames();
  }, [chainId]);

  if (!isConnected) {
    return (
      <ConnectWallet title={'common.connectWallet'} description={'pages.dailyQuests.connectDesc'} />
    );
  }

  return (
    <>
      <AnimatedTransition show={showSignModal}>
        <SignModal />
      </AnimatedTransition>
      <AnimatedTransition show={rpcErrorModal.show}>
        <RpcErrorModal />
      </AnimatedTransition>
      {isMobile ? (
        <MobileView
          key={chainId}
          approveAllowanceHandler={approveAllowanceHandler}
          startGameHandler={startGameHandler}
          joinRoomHandler={joinRoomHandler}
          games={games}
          activeGames={gamesInfo}
        />
      ) : (
        <DesktopView
          key={chainId}
          approveAllowanceHandler={approveAllowanceHandler}
          startGameHandler={startGameHandler}
          joinRoomHandler={joinRoomHandler}
          games={games}
          activeGames={gamesInfo}
        />
      )}
    </>
  );
};
